import { AfterViewInit, Directive, ElementRef, Input, NgZone } from '@angular/core';

@Directive({
  selector: '[countUp]',
  standalone: true,
})
export class CountUpDirective implements AfterViewInit {
  @Input() duration = 1200; // ms
  @Input() start = 0;

  private parsedTarget = 0;
  private suffix = '';
  private decimals = 0;
  private hasInitialized = false;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    // Parse initial content as target value + suffix
    const original = (this.el.nativeElement.textContent || '').trim();
    const match = original.match(/^(\d+(?:[.,]\d+)?)(.*)$/);
    if (match) {
      const numPart = match[1].replace(',', '.');
      const suffix = match[2] || '';
      this.parsedTarget = parseFloat(numPart);
      this.suffix = suffix;
      this.decimals = numPart.includes('.') ? (numPart.split('.')[1]?.length || 0) : 0;
    } else {
      // Fallback: no number found, do nothing
      return;
    }

    // Initialize display to start value
    this.writeValue(this.start);

    // Start when visible
    this.zone.runOutsideAngular(() => {
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.hasInitialized) {
            this.hasInitialized = true;
            io.unobserve(this.el.nativeElement);
            this.animate();
            break;
          }
        }
      }, { threshold: 0.15, rootMargin: '0px 0px 180px 0px' });
      io.observe(this.el.nativeElement);
    });
  }

  private animate() {
    const startTime = performance.now();
    const startVal = this.start;
    const target = this.parsedTarget;
    const duration = Math.max(300, this.duration);

    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = this.easeOutCubic(t);
      const current = startVal + (target - startVal) * eased;
      this.writeValue(current);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        // Ensure final precise value
        this.writeFinalValue(target);
      }
    };

    requestAnimationFrame(step);
  }

  private writeValue(value: number) {
    const v = this.formatNumber(value);
    this.el.nativeElement.textContent = `${v}${this.suffix}`;
  }

  private writeFinalValue(value: number) {
    const v = this.formatNumber(value, true);
    this.el.nativeElement.textContent = `${v}${this.suffix}`;
  }

  private formatNumber(value: number, final = false): string {
    // For integers, avoid decimal flickering
    if (this.decimals === 0) {
      const rounded = final ? Math.round(value) : Math.floor(value);
      return String(rounded);
    }
    // For decimals, keep configured precision
    return value.toFixed(this.decimals);
  }

  private easeOutCubic(x: number): number {
    return 1 - Math.pow(1 - x, 3);
  }
}